import React from 'react';
import styled from 'styled-components';

import { useAnchoredPanel } from './useAnchoredPanel';

/**
 * 劃假的精簡選單：假別（特休／一般休）× 時段（全天／上半天／下半天）＝ 6 個按鈕，
 * 加上「取消劃假」。
 *
 * ⚠️ 這個選單 **只在右鍵（桌機）或點一下（觸控裝置）時** 打開。
 * 桌機左鍵是快捷：直接畫「一般休・整天」，⛔ 不開這個選單 —— 見 index.js 的 handleCellClick。
 *
 * ⛔ 這裡刻意不出現「預假」：預假是提前選放假的流程名稱，不是假別。
 * ⛔ 也不出現預假順位／最低在班／雙診等規則面板，員工端不阻擋輸入。
 *
 * ⚠️ 面板的定位規則（含捲動時重新定位、Esc 關閉）收在 useAnchoredPanel，⛔ 不在這裡各寫一份。
 */
const Backdrop = styled.div`
    position: fixed;
    inset: 0;
    z-index: 1040;
    background: transparent;
`;

const Panel = styled.div`
    position: fixed;
    z-index: 1050;
    width: 286px;
    background: #fff;
    border: 1px solid #cfd6dd;
    border-radius: 6px;
    box-shadow: 0 8px 24px rgba(31, 41, 51, 0.16);
    padding: 11px 12px;
    font-family: 'Noto Sans TC', sans-serif;

    .picker-title {
        font-size: 13px;
        font-weight: bold;
        color: #1f2933;
        margin-bottom: 1px;
    }

    .picker-hint {
        font-size: 11px;
        color: #7b8794;
        margin-bottom: 8px;
        padding-bottom: 7px;
        border-bottom: 1px solid #eef1f4;
    }

    .picker-row {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-bottom: 6px;
    }

    .picker-label {
        width: 52px;
        flex-shrink: 0;
        font-size: 12px;
        font-weight: bold;
    }
`;

const OptionButton = styled.button`
    flex: 1;
    border: 1px solid ${(props) => (props.selected ? props.accent : '#cfd6dd')};
    background: ${(props) => (props.selected ? props.accent : '#fff')};
    color: ${(props) => (props.selected ? '#fff' : '#4b5563')};
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 0;
    cursor: pointer;

    &:hover:not(:disabled) {
        border-color: ${(props) => props.accent};
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ClearButton = styled.button`
    width: 100%;
    margin-top: 6px;
    border: 1px solid #f0bcbc;
    background: #fff;
    color: #c92a2a;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    padding: 6px 0;
    cursor: pointer;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const MarkPicker = ({ anchorSelector, target, categories, periods, onPick, onClear, onClose, busy }) => {
    const { panelRef, pos } = useAnchoredPanel(anchorSelector, onClose);

    if (!anchorSelector || !target) return null;

    const { date, mark } = target;

    return (
        <React.Fragment>
            <Backdrop
                onClick={onClose}
                onContextMenu={(e) => {
                    // 選單開著時在別處按右鍵：關掉選單就好，⛔ 不要讓瀏覽器原生選單蓋上來
                    e.preventDefault();
                    onClose();
                }}
            />
            <Panel ref={panelRef} style={pos} data-testid="mark-picker">
                <div className="picker-title">{date.date}（{date.weekday}）</div>
                <div className="picker-hint">
                    {mark ? `目前：${mark.category_label}・${mark.period_label}` : '目前：上班（預設）'}
                </div>

                {categories.map((cat) => (
                    <div className="picker-row" key={cat.value}>
                        <span className="picker-label" style={{ color: cat.color }}>
                            {cat.label}
                        </span>
                        {periods.map((p) => (
                            <OptionButton
                                key={`${cat.value}-${p.value}`}
                                type="button"
                                data-category={cat.value}
                                data-period={p.value}
                                accent={cat.color}
                                disabled={busy}
                                selected={!!mark && mark.category === cat.value && mark.period === p.value}
                                onClick={() => onPick(cat.value, p.value)}
                            >
                                {p.label}
                            </OptionButton>
                        ))}
                    </div>
                ))}

                <ClearButton type="button" data-action="clear-mark" disabled={busy || !mark} onClick={onClear}>
                    {mark ? '取消劃假（改回上班）' : '這天原本就是上班'}
                </ClearButton>
            </Panel>
        </React.Fragment>
    );
};

export default MarkPicker;
